import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import type { IReembedKBResponse, KBType } from 'features/chat/agent-chat-module/types/kb.service.type';
import { useReembedKb } from 'features/chat/hooks/use-kb';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';

type KbReembedDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectKey: string;
  kbs: { id: string; type: KBType }[];
  documentName?: string;
  onSuccess?: () => void;
};

const isReembedSuccessful = (response: IReembedKBResponse) => {
  if (response.is_success === false || response.success === false) {
    return false;
  }

  return response.is_success === true || response.success === true || response.is_success === undefined;
};

export function KbReembedDialog({
  open,
  onOpenChange,
  projectKey,
  kbs,
  documentName,
  onSuccess,
}: KbReembedDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync, isPending } = useReembedKb();

  const handleConfirm = async () => {
    if (!projectKey) {
      toast({
        title: t('RE_EMBED_FAILED') || 'Re-embed failed',
        description: t('MISSING_PROJECT_KEY') || 'Project key is missing.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await mutateAsync({
        project_key: projectKey,
        kbs,
      });

      if (!isReembedSuccessful(response)) {
        throw new Error(
          typeof response.error === 'string'
            ? response.error
            : response.detail || response.message || t('FAILED_TO_RE_EMBED') || 'Failed to start re-embedding.'
        );
      }

      toast({
        variant: 'info',
        title: t('RE_EMBED_STARTED') || 'Re-embed started',
        description: t('RE_EMBED_STARTED_SUCCESSFULLY') || 'Knowledge base re-embedding started successfully.',
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('RE_EMBED_FAILED') || 'Re-embed failed',
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const description = documentName
    ? t('RE_EMBED_CONFIRMATION_DESCRIPTION', { name: documentName }) ||
      `Are you sure you want to re-embed "${documentName}"?`
    : t('RE_EMBED_CONFIRMATION') || 'Are you sure you want to re-embed?';

  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('RE_EMBED') || 'Re-embed'}
      description={description}
      onConfirm={handleConfirm}
      confirmText={t('RE_EMBED') || 'Re-embed'}
      cancelText={t('CANCEL') || 'Cancel'}
      preventAutoClose
      isLoading={isPending}
    />
  );
}
